/**
 * Shared core for async-action orchestration used by both the
 * `useAsyncAction` React hook and the `runAsyncAction` thunk-side utility.
 *
 * Encapsulates the recurring pattern found across `schemaManagementInterface`,
 * `sypi-interface`, and `file-interface`:
 *
 *   dispatch(showSpinner());
 *   try {
 *     const result = await adapter.doThing();
 *     dispatch(loadThing(result));
 *     dispatch(stopSpinner());
 *   } catch (error) {
 *     dispatch(reportError(userErrorMessage('update', 'thing', error)));
 *     dispatch(stopSpinner());
 *   }
 *
 * Consumers pass a bare async function plus declarative options for the
 * spinner / success-toast / error surface, and the core wires the dispatches
 * in the correct order. Success and failure callbacks let callers keep their
 * state-updating dispatches co-located with the call site.
 *
 * Dispatch ordering (locked by tests):
 *   success → stopSpinner → setToastMessage → onSuccess
 *   failure → stopSpinner → error surface   → onFailure
 *
 * `stopSpinner` is dispatched before any user-visible message so the spinner
 * overlay never covers a toast/alert. This matches the legacy interfaces.
 */

import {
  showSpinner,
  stopSpinner,
  reportError,
  setToastMessage,
  setAlertDock,
} from "@actions/global";
import { userErrorMessage } from "@utils/custom-errors";

// Loose action / dispatch shapes: the async-action core is agnostic to the
// specific typed dispatch used by callers (AppDispatch, thunk dispatch, or
// the plain useDispatch return). Anything with a `dispatch(action)` signature
// works.
export type DispatchLike = (action: any) => any;

export type ErrorSurface = "report" | "alert" | "toast";

export interface AsyncActionOptions<T = unknown> {
  /** Show the global spinner while the action runs. Default: true. */
  spinner?: boolean;
  /**
   * Success toast text, or a builder receiving the resolved value.
   * If omitted, no success toast is dispatched.
   */
  successToast?: string | ((value: T) => string);
  /** Optional callback attached to the success toast dispatch. */
  successToastCallback?: () => void;
  /**
   * Noun for the built error message (feeds `userErrorMessage`).
   * e.g. `"file"`, `"config"`, `"schema"`.
   */
  errorSubject?: string;
  /**
   * Verb for the built error message (feeds `userErrorMessage`).
   * e.g. `"update"`, `"delete"`, `"import"`.
   */
  errorAction?: string;
  /**
   * How to surface an error. Default: `"report"` (dispatches `reportError`,
   * which under the hood is an alert dock with the title "Error").
   *
   * - `"alert"` — dispatches `setAlertDock` with `errorTitle` + message
   * - `"toast"` — dispatches `setToastMessage` with the error string
   */
  onError?: ErrorSurface;
  /** Title used when `onError === "alert"`. Default: `"Error"`. */
  errorTitle?: string;
  /**
   * Overrides the built error message. Receives the raw thrown error.
   * When omitted, `userErrorMessage(errorAction, errorSubject, error.message)`
   * is used.
   */
  buildErrorMessage?: (error: unknown) => string;
  /** Skip dispatching any error UI (still stops spinner and calls onFailure). */
  suppressErrorReport?: boolean;
  /** Called after the success dispatches with the resolved value. */
  onSuccess?: (value: T) => void;
  /** Called after the failure dispatches with the caught error. */
  onFailure?: (error: unknown) => void;
  /** Re-throw the caught error after failure dispatches. Default: false. */
  rethrow?: boolean;
}

export type AsyncActionResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: unknown };

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Runs `action` inside the shared spinner/toast/error scaffolding.
 * Returns a discriminated result — never throws unless `rethrow` is true.
 */
export async function runAsyncActionCore<T>(
  dispatch: DispatchLike,
  action: () => Promise<T>,
  options: AsyncActionOptions<T> = {}
): Promise<AsyncActionResult<T>> {
  const {
    spinner = true,
    successToast,
    successToastCallback,
    errorSubject,
    errorAction,
    onError = "report",
    errorTitle = "Error",
    buildErrorMessage,
    suppressErrorReport = false,
    onSuccess,
    onFailure,
    rethrow = false,
  } = options;

  if (spinner) dispatch(showSpinner());

  try {
    const value = await action();

    if (spinner) dispatch(stopSpinner());

    if (successToast !== undefined) {
      const message =
        typeof successToast === "function" ? successToast(value) : successToast;
      dispatch(setToastMessage(message, successToastCallback));
    }

    onSuccess?.(value);
    return { ok: true, value };
  } catch (error) {
    if (spinner) dispatch(stopSpinner());

    if (!suppressErrorReport) {
      const message = buildErrorMessage
        ? buildErrorMessage(error)
        : userErrorMessage(errorAction, errorSubject, toErrorMessage(error));

      if (onError === "alert") {
        dispatch(setAlertDock({ title: errorTitle, message }));
      } else if (onError === "toast") {
        dispatch(setToastMessage(message));
      } else {
        dispatch(reportError(message));
      }
    }

    onFailure?.(error);

    if (rethrow) throw error;
    return { ok: false, error };
  }
}
